import * as Sentry from "@sentry/react";

import type {
  GroupColorSlot,
  WorkspaceDateRange,
} from "../workspace/workspace.types";
import {
  getNextGroupId,
  normalizeWorkspaceSymbol,
} from "../workspace/workspace.utils";
import {
  createGroupRegistryChannel,
  type GroupRegistryChannel,
  type GroupRegistryMessage,
} from "./groupRegistry.channel";
import {
  canonicalGroupRegistryDocument,
  createDefaultGroupRegistryDocument,
  createGlobalGroup,
  type GlobalGroupId,
  type GroupRegistryDocument,
  groupRegistryDocumentsEqual,
  parseGroupRegistryDocument,
  sortGlobalGroups,
} from "./groupRegistry.document";
import {
  createGroupRegistryLock,
  type GroupRegistryLock,
} from "./groupRegistry.lock";
import {
  GROUP_REGISTRY_STORAGE_KEY,
  type GroupRegistryStorage,
  readGroupRegistryDocument,
  writeGroupRegistryDocument,
} from "./groupRegistry.storage";

export class GroupRegistryWriteError extends Error {
  constructor() {
    super("Unable to save global group changes");
    this.name = "GroupRegistryWriteError";
  }
}

export class GroupRegistryValidationError extends Error {
  constructor() {
    super("Invalid global group update");
    this.name = "GroupRegistryValidationError";
  }
}

export class GroupRegistryDisposedError extends Error {
  constructor() {
    super("The global group registry has been disposed");
    this.name = "GroupRegistryDisposedError";
  }
}

export const reportGroupRegistryError = (error: unknown) => {
  Sentry.captureException(error, {
    tags: { component: "group-registry" },
  });
};

export interface GroupRegistryRepository {
  getSnapshot: () => GroupRegistryDocument;
  subscribe: (listener: () => void) => () => void;
  createGroup: (options?: {
    seedSymbol?: string;
  }) => Promise<GlobalGroupId | undefined>;
  deleteGroup: (id: GlobalGroupId) => Promise<void>;
  updateGroup: (
    id: GlobalGroupId,
    update: GroupRegistryDefinitionUpdate,
  ) => Promise<void>;
  setGroupName: (id: GlobalGroupId, name: string) => Promise<void>;
  setGroupColor: (
    id: GlobalGroupId,
    colorSlot: GroupColorSlot,
  ) => Promise<void>;
  setGroupSymbol: (id: GlobalGroupId, symbol?: string) => Promise<void>;
  setGroupDateRange: (
    id: GlobalGroupId,
    range?: WorkspaceDateRange,
  ) => Promise<void>;
  flush: () => Promise<void>;
  dispose: () => void;
  isDisposed: () => boolean;
}

export interface GroupRegistryDefinitionUpdate {
  name?: string | undefined;
  colorSlot?: GroupColorSlot | undefined;
  symbol?: string | undefined;
  dateRange?: WorkspaceDateRange | undefined;
}

type RegistryGroup = GroupRegistryDocument["groups"][number];

const applyNameUpdate = (
  group: RegistryGroup,
  update: GroupRegistryDefinitionUpdate,
): boolean => {
  if (!Object.hasOwn(update, "name")) {
    return false;
  }
  const normalized = update.name?.trim().slice(0, 100) || undefined;
  if (group.name === normalized) {
    return false;
  }
  if (normalized) {
    group.name = normalized;
  } else {
    delete group.name;
  }
  return true;
};

const applyColorUpdate = (
  group: RegistryGroup,
  update: GroupRegistryDefinitionUpdate,
): boolean => {
  if (
    !Object.hasOwn(update, "colorSlot") ||
    update.colorSlot === undefined ||
    group.colorSlot === update.colorSlot
  ) {
    return false;
  }
  group.colorSlot = update.colorSlot;
  return true;
};

const applySymbolUpdate = (
  group: RegistryGroup,
  update: GroupRegistryDefinitionUpdate,
): boolean => {
  if (!Object.hasOwn(update, "symbol")) {
    return false;
  }
  const normalized = update.symbol
    ? normalizeWorkspaceSymbol(update.symbol) || undefined
    : undefined;
  if (group.linkState.symbol === normalized) {
    return false;
  }
  if (normalized) {
    group.linkState.symbol = normalized;
  } else {
    delete group.linkState.symbol;
  }
  return true;
};

const applyDateRangeUpdate = (
  group: RegistryGroup,
  update: GroupRegistryDefinitionUpdate,
): boolean => {
  if (
    !Object.hasOwn(update, "dateRange") ||
    JSON.stringify(group.linkState.dateRange) ===
      JSON.stringify(update.dateRange)
  ) {
    return false;
  }
  if (update.dateRange) {
    group.linkState.dateRange = { ...update.dateRange };
  } else {
    delete group.linkState.dateRange;
  }
  return true;
};

export interface CreateGroupRegistryRepositoryOptions {
  channel?: GroupRegistryChannel;
  createChannel?: () => GroupRegistryChannel;
  writerId?: string;
  storage?: GroupRegistryStorage;
  lock?: GroupRegistryLock;
}

const selectWinner = (
  first: GroupRegistryDocument,
  ...candidates: Array<GroupRegistryDocument | undefined>
): GroupRegistryDocument =>
  candidates.reduce<GroupRegistryDocument>((winner, candidate) => {
    if (!candidate || candidate.revision < winner.revision) {
      return winner;
    }
    if (candidate.revision > winner.revision) {
      return candidate;
    }
    return groupRegistryDocumentsEqual(candidate, winner)
      ? winner
      : canonicalGroupRegistryDocument(candidate, winner);
  }, first);

type GroupMutation = (
  document: GroupRegistryDocument,
) => GroupRegistryDocument | undefined;

export const createGroupRegistryRepository = (
  options: CreateGroupRegistryRepositoryOptions = {},
): GroupRegistryRepository => {
  const storage = options.storage ?? window.localStorage;
  const lock = options.lock ?? createGroupRegistryLock();
  const ownsChannel = options.channel === undefined;
  const channel =
    options.channel ??
    options.createChannel?.() ??
    createGroupRegistryChannel();
  const writerId = options.writerId ?? crypto.randomUUID();
  const listeners = new Set<() => void>();
  let disposed = false;
  let lifecycleGeneration = 0;
  let current =
    readGroupRegistryDocument(storage) ?? createDefaultGroupRegistryDocument();
  let pending: Promise<unknown> = Promise.resolve();

  const notify = () => {
    for (const listener of [...listeners]) {
      listener();
    }
  };

  const isActive = (generation: number) =>
    !disposed && lifecycleGeneration === generation;

  const assertActive = (generation: number) => {
    if (!isActive(generation)) {
      throw new GroupRegistryDisposedError();
    }
  };

  const adopt = (document: GroupRegistryDocument, generation: number) => {
    if (
      !isActive(generation) ||
      groupRegistryDocumentsEqual(current, document)
    ) {
      return;
    }
    current = structuredClone(document);
    notify();
  };

  const enqueue = <T>(operation: () => Promise<T>): Promise<T> => {
    const run = pending.then(operation, operation);
    pending = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  };

  const persistAndAnnounce = (
    document: GroupRegistryDocument,
    generation: number,
  ) => {
    assertActive(generation);
    if (!writeGroupRegistryDocument(document, storage)) {
      throw new GroupRegistryWriteError();
    }
    assertActive(generation);
    adopt(document, generation);
    assertActive(generation);
    channel.post({ writerId, document });
  };

  const seedGeneration = lifecycleGeneration;
  const seed = enqueue(() =>
    lock.run(() => {
      if (!isActive(seedGeneration)) {
        return;
      }
      const stored = readGroupRegistryDocument(storage);
      if (stored) {
        adopt(stored, seedGeneration);
        return;
      }
      persistAndAnnounce(current, seedGeneration);
    }),
  );
  void seed.catch(reportGroupRegistryError);

  const reconcileRemote = (document: GroupRegistryDocument) => {
    const generation = lifecycleGeneration;
    return enqueue(() =>
      lock.run(() => {
        if (!isActive(generation)) {
          return;
        }
        const stored = readGroupRegistryDocument(storage);
        const winner = selectWinner(document, stored, current);
        if (!stored || !groupRegistryDocumentsEqual(stored, winner)) {
          persistAndAnnounce(winner, generation);
        } else {
          adopt(winner, generation);
        }
      }),
    );
  };

  const acceptRemote = (message: GroupRegistryMessage) => {
    if (disposed || message.writerId === writerId) {
      return;
    }
    const remote = message.document;
    if (remote.revision < current.revision) {
      return;
    }
    if (
      remote.revision === current.revision &&
      groupRegistryDocumentsEqual(remote, current)
    ) {
      return;
    }
    void reconcileRemote(remote).catch(reportGroupRegistryError);
  };

  const unsubscribeChannel = channel.subscribe(acceptRemote);
  const handleStorage = (event: StorageEvent) => {
    if (
      disposed ||
      (event.key !== null && event.key !== GROUP_REGISTRY_STORAGE_KEY)
    ) {
      return;
    }
    const document = readGroupRegistryDocument(storage);
    if (document) {
      acceptRemote({ writerId: "", document });
    }
  };
  window.addEventListener("storage", handleStorage);

  const commit = (mutation: GroupMutation): Promise<void> => {
    if (disposed) {
      return Promise.reject(new GroupRegistryDisposedError());
    }
    const generation = lifecycleGeneration;
    return enqueue(() => {
      assertActive(generation);
      return lock.run(() => {
        assertActive(generation);
        const stored = readGroupRegistryDocument(storage);
        const base = selectWinner(current, stored);
        const mutated = mutation(structuredClone(base));
        if (!mutated) {
          adopt(base, generation);
          return;
        }
        const next = parseGroupRegistryDocument({
          ...mutated,
          schemaVersion: 3,
          revision: base.revision + 1,
        });
        if (!next) {
          throw new GroupRegistryValidationError();
        }
        persistAndAnnounce(next, generation);
      });
    });
  };

  const mutateGroup = (
    id: GlobalGroupId,
    mutation: (group: GroupRegistryDocument["groups"][number]) => boolean,
  ) =>
    commit((document) => {
      const group = document.groups.find((candidate) => candidate.id === id);
      return group && mutation(group) ? document : undefined;
    });

  const updateGroup = (
    id: GlobalGroupId,
    update: GroupRegistryDefinitionUpdate,
  ): Promise<void> =>
    mutateGroup(id, (group) =>
      [
        applyNameUpdate(group, update),
        applyColorUpdate(group, update),
        applySymbolUpdate(group, update),
        applyDateRangeUpdate(group, update),
      ].includes(true),
    );

  const createGroup = (options?: {
    seedSymbol?: string;
  }): Promise<GlobalGroupId | undefined> => {
    if (disposed) {
      return Promise.reject(new GroupRegistryDisposedError());
    }
    let createdId: GlobalGroupId | undefined;
    return commit((document) => {
      const occupied = document.groups.map((group) => group.id);
      const id = getNextGroupId(occupied);
      if (id === undefined) {
        return undefined;
      }
      createdId = id;
      document.groups = sortGlobalGroups([
        ...document.groups,
        createGlobalGroup(id, { seedSymbol: options?.seedSymbol }),
      ]);
      return document;
    }).then(() => createdId);
  };

  const deleteGroup = (id: GlobalGroupId): Promise<void> =>
    commit((document) => {
      const nextGroups = document.groups.filter((group) => group.id !== id);
      if (nextGroups.length === document.groups.length) {
        return undefined;
      }
      document.groups = nextGroups;
      return document;
    });

  return {
    getSnapshot: () => current,
    subscribe: (listener) => {
      if (disposed) {
        return () => {};
      }
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    createGroup,
    deleteGroup,
    updateGroup,
    setGroupName: (id, name) => updateGroup(id, { name }),
    setGroupColor: (id, colorSlot) => updateGroup(id, { colorSlot }),
    setGroupSymbol: (id, symbol) => updateGroup(id, { symbol }),
    setGroupDateRange: (id, dateRange) => updateGroup(id, { dateRange }),
    flush: async () => {
      await pending;
    },
    dispose: () => {
      if (disposed) {
        return;
      }
      disposed = true;
      lifecycleGeneration += 1;
      listeners.clear();
      unsubscribeChannel();
      window.removeEventListener("storage", handleStorage);
      if (ownsChannel) {
        channel.close();
      }
    },
    isDisposed: () => disposed,
  };
};
